import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Checkbox } from '@/components/ui/Checkbox';
import { TimeInput } from '@/components/ui/TimeInput';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  SaveBar,
  SectionCard,
  SettingsChoiceRow,
  SettingsLoading,
  SettingsStack,
} from './settingsChrome';
import type { Json } from '@/types/database.generated';

const WEEKDAYS = ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lordag', 'sondag'] as const;
type Weekday = (typeof WEEKDAYS)[number];

interface DayHours {
  enabled: boolean;
  start: string;
  end: string;
}
type BusinessHoursShape = Record<Weekday, DayHours>;

const DEFAULT_BUSINESS_HOURS: BusinessHoursShape = {
  mandag: { enabled: true, start: '08:00', end: '16:00' },
  tirsdag: { enabled: true, start: '08:00', end: '16:00' },
  onsdag: { enabled: true, start: '08:00', end: '16:00' },
  torsdag: { enabled: true, start: '08:00', end: '16:00' },
  fredag: { enabled: true, start: '08:00', end: '16:00' },
  lordag: { enabled: false, start: '09:00', end: '14:00' },
  sondag: { enabled: false, start: '09:00', end: '14:00' },
};

function readBusinessHours(value: Json): BusinessHoursShape {
  const out = { ...DEFAULT_BUSINESS_HOURS };
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    for (const day of WEEKDAYS) {
      const v = (value as Record<string, Json>)[day];
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const obj = v as Record<string, Json>;
        out[day] = {
          enabled: Boolean(obj.enabled),
          start: typeof obj.start === 'string' ? obj.start : DEFAULT_BUSINESS_HOURS[day].start,
          end: typeof obj.end === 'string' ? obj.end : DEFAULT_BUSINESS_HOURS[day].end,
        };
      }
    }
  }
  return out;
}

export function ClinicHours() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const { data: clinic, isLoading, error } = useClinic(activeClinicId);
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const [hours, setHours] = useState<BusinessHoursShape>(DEFAULT_BUSINESS_HOURS);

  const applyClinic = (row: NonNullable<typeof clinic>) => {
    setHours(readBusinessHours(row.business_hours));
  };

  useEffect(() => {
    if (!clinic) return;
    applyClinic(clinic);
    // Seed from the loaded clinic row only; applyClinic closes over setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic]);

  if (isLoading) return <SettingsLoading />;
  if (error) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await update.mutateAsync({ business_hours: hours as unknown as Json });
      toast.success(t('settings.saved_toast'));
    } catch (err) {
      console.error('update_clinic_settings (business_hours) failed', err);
      toast.error(t('settings.save_error_toast'));
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex min-w-0 flex-col gap-s4">
      <SectionCard
        title={t('settings.hours.title')}
        subtitle={t('settings.hours.subtitle')}
        readOnly={!canEdit}
      >
        <SettingsStack>
          {WEEKDAYS.map((day) => {
            const dh = hours[day];
            return (
              <SettingsChoiceRow
                key={day}
                className="flex-col items-stretch gap-s3 nav:flex-row nav:items-center"
              >
                <div className="w-full shrink-0 nav:w-36">
                  <Checkbox
                    checked={dh.enabled}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setHours((h) => ({
                        ...h,
                        [day]: { ...h[day], enabled: e.target.checked },
                      }))
                    }
                    label={t(`settings.hours.weekdays.${day}`)}
                  />
                </div>

                {dh.enabled ? (
                  <div className="flex min-w-0 flex-wrap items-center gap-s2">
                    <TimeInput
                      value={dh.start}
                      disabled={!canEdit}
                      onChange={(start) =>
                        setHours((h) => ({
                          ...h,
                          [day]: { ...h[day], start },
                        }))
                      }
                    />
                    <span className={cn(TYPE.small, 'text-ds-muted')}>{t('settings.hours.to')}</span>
                    <TimeInput
                      value={dh.end}
                      disabled={!canEdit}
                      onChange={(end) =>
                        setHours((h) => ({
                          ...h,
                          [day]: { ...h[day], end },
                        }))
                      }
                    />
                  </div>
                ) : (
                  <span className={cn(TYPE.small, 'italic text-ds-muted')}>
                    {t('settings.hours.closed')}
                  </span>
                )}
              </SettingsChoiceRow>
            );
          })}
        </SettingsStack>
      </SectionCard>

      <SaveBar
        canEdit={canEdit}
        pending={update.isPending}
        onCancel={() => applyClinic(clinic)}
      />
    </form>
  );
}
